// Worker
import { Worker } from "../Worker";
import { extractEntityRoutingIdFromSubRoute, extractMyTasksRoutingDetailsFromSubRoute, extractPlanRoutingDetailsFromSubRoute, extractPrimaryViewIdFromRoute, getTaskIdFromSubRoute } from "@ms/uno-routing/lib/local/utilities/RouteUtilities";
import { TraceLevel } from "@ms/uno-telemetry/lib/local/events/Trace.event";
import { Temporal } from "temporal-polyfill-lite";
// Constants
import { ClientFlavor, ClientType } from "@ms/uno-constants/lib/local/AppConstants";
import { DataPreFetcherEventNames } from "@ms/uno-constants/lib/local/DataPreFetcherConstants";
import { SubRouteType } from "@ms/uno-constants/lib/local/RoutingConstants";
import { ViewDataFetchState } from "@ms/uno-constants/lib/local/ViewConstants";
// Models
import { TaskEditorTab } from "@ms/planner-models/lib/local/client/planner/Enumerations";
import { TaskEditorSidePanelType } from "@ms/planner-models/lib/local/types/TaskEditorSidePanelType";
import { Store } from "@ms/planner-stores/lib/local/stores/Store";
// Action creator
import { ActionCreator } from "@ms/planner-actioncreators/lib/local/actioncreators/ActionCreator";
/**
 * Decision produced by `evaluateFetchDecision` describing what the worker should do for the
 * current invocation of `fetchViewDataBasedOnRoute`.
 */ export var FetchDecision = /*#__PURE__*/ function(FetchDecision) {
    /** Bail without touching `lastRoute` — guarded by background/prefetcher/in-flight checks. */ FetchDecision["Skip"] = "skip";
    /** Bail but refresh `lastRoute` — route delta is too small to warrant a fetch. */ FetchDecision["SyncRouteOnly"] = "syncRouteOnly";
    /** Proceed with the route-specific fetch. */ FetchDecision["Fetch"] = "fetch";
    return FetchDecision;
}({});
/**
 * Data fetch worker
 */ export class DataFetchWorker {
    init() {
        if (this.initialized) {
            return;
        }
        // Fetch the data for the initial route if the app is loaded in foreground.
        if (!this.appContextStore.isAppInBackground()) {
            this.fetchDataForTheInitialRoute();
            // Start the periodic data fetch polling after initial data fetch
            this.startDataFetchPolling();
        }
        // Subscribe to routeStore to fetch view data based on route change,
        // not waiting on the respective view to be loaded
        this.routeStore.subscribeToStore(this.fetchViewDataBasedOnRoute);
        this.appContextStore.subscribeToStore(this.handleAppBackgroundStateUpdate);
        this.trouterStore.subscribeToStore(this.handleTrouterConnectionStateChange);
        this.authStore.subscribeToStore(this.handleAuthStateChange);
        if (this.configProvider().flights.EnableDependencies || this.configProvider().flights.EnableTaskHistory) {
            this.viewStore.subscribeToStore(this.handleViewStoreChange);
        }
        // Keep history in sync with the open task. The route (fetchViewDataBasedOnRoute) and view
        // (handleViewStoreChange) callbacks call the same method; the task-store subscription covers
        // task edits while the pane is open.
        if (this.configProvider().flights.EnableTaskHistory) {
            this.taskStore.subscribeToStore(this.handleTaskChangeForHistory);
        }
        this.initialized = true;
        this.wasAppInBackground = this.appContextStore.isAppInBackground();
        this.lastTrouterConnectedState = this.trouterStore.getConnectionState().isConnected;
    }
    get name() {
        return Worker.DataFetch;
    }
    /**
     * Stops the worker by removing the event listeners
     */ stop() {
        this.routeStore.unsubscribeFromStore(this.fetchViewDataBasedOnRoute);
        this.appContextStore.unsubscribeFromStore(this.handleAppBackgroundStateUpdate);
        this.trouterStore.unsubscribeFromStore(this.handleTrouterConnectionStateChange);
        this.authStore.unsubscribeFromStore(this.handleAuthStateChange);
        if (this.configProvider().flights.EnableDependencies || this.configProvider().flights.EnableTaskHistory) {
            this.viewStore.unsubscribeFromStore(this.handleViewStoreChange);
        }
        if (this.configProvider().flights.EnableTaskHistory) {
            this.taskStore.unsubscribeFromStore(this.handleTaskChangeForHistory);
        }
        this.clearTrouterDebounceTimer();
        this.stopDataFetchPolling();
        this.initialized = false;
        this.lastRoute = undefined;
        this.lastTaskEditorSelectedTab = undefined;
        this.lastFetchedHistoryTask = undefined;
    }
    /**
     * Clear any pending trouter connection-state debounce timer.
     */ clearTrouterDebounceTimer() {
        if (this.trouterDebounceTimerId !== undefined) {
            window.clearTimeout(this.trouterDebounceTimerId);
            this.trouterDebounceTimerId = undefined;
        }
    }
    /**
     * Fetch the data for the initial route
     */ async fetchDataForTheInitialRoute() {
        if (!this.configProvider().settings.disablePreFetcher) {
            // Wait for data pre-fetch completion before triggering data fetch for initial route
            await this.waitForDataPreFetchCompletion();
        }
        this.fetchPlannerViewData();
        this.fetchUpsellData();
        this.fetchViewDataBasedOnRoute();
        this.fetchLeftNavViewData();
        this.fetchSuiteNavViewData();
    }
    /**
     * Evaluate the current trouter connection state after the debounce window.
     * Only acts when the post-debounce state differs from the last-reacted state —
     * a flap that returns to the original state is absorbed with zero work.
     */ evaluateTrouterStateChange() {
        const currentConnected = this.trouterStore.getConnectionState().isConnected;
        if (currentConnected === this.lastTrouterConnectedState) {
            return;
        }
        const wasConnected = this.lastTrouterConnectedState;
        this.lastTrouterConnectedState = currentConnected;
        const newIntervalMs = this.getPollingIntervalMs();
        this.loggers.traceLogger.logTrace(0x1e061563 /* tag_4b7v9 */ , TraceLevel.Info, `Trouter connection state changed (debounced), adjusting polling interval ` + `[WasConnected=${wasConnected}][IsConnected=${currentConnected}][NewIntervalMs=${newIntervalMs}]`);
        // Only restart polling when the app is in the foreground and delta sync is enabled:
        // - Background: handleAppBackgroundStateUpdate already stopped polling; restarting here
        //   would defeat that and waste resources on an unobserved app.
        // - Delta sync disabled: polling cadence doesn't depend on trouter connectivity, so a
        //   reconnect/disconnect doesn't warrant a restart.
        if (!this.appContextStore.isAppInBackground() && !this.configProvider().settings.deltaSyncSettings.disable) {
            this.restartDataFetchPolling(true);
        }
    }
    /**
     * Check if user is active
     */ isUserActive() {
        if (this.configProvider().changeGates.DisableInactivityGate) {
            return true;
        }
        const lastActivity = this.activityStore.getLastUserActivityTimestamp();
        const elapsedMs = Temporal.Now.instant().epochMilliseconds - lastActivity.epochMilliseconds;
        return elapsedMs < this.configProvider().settings.inactivityThresholdMs;
    }
    /**
     * When the user switches task editor tabs, trigger a data fetch for the new tab.
     */ handleTaskEditorTabChange() {
        const subRoute = this.routeStore.getCurrentSubRoute();
        const taskId = getTaskIdFromSubRoute(subRoute);
        const currentTab = taskId ? this.viewStore.getTaskEditorSelectedTab(taskId) : undefined;
        const currentSubRouteType = this.routeStore.getCurrentSubRouteType();
        if (currentTab !== this.lastTaskEditorSelectedTab && (currentSubRouteType === SubRouteType.MyDay || currentSubRouteType === SubRouteType.MyTasks)) {
            this.lastTaskEditorSelectedTab = currentTab;
            const shouldFetchTasksForDependency = currentTab === TaskEditorTab.Planning;
            this.fetchTaskEditorViewData(/* isBackgroundSync */ false, undefined, shouldFetchTasksForDependency);
        }
    }
    /**
     * Check if the data should be fetched for the left nav
     */ shouldFetchDataForLeftNav() {
        const { clientFlavor } = this.configProvider().sessionMetaData.appMetadata;
        const subRouteType = this.routeStore.getCurrentSubRouteType();
        return clientFlavor !== ClientFlavor.TaskFromMessage && clientFlavor !== ClientFlavor.Tab && subRouteType !== SubRouteType.ConfigTeamsTab && subRouteType !== SubRouteType.RemoveTeamsTab;
    }
    /**
     * Fetch planner view data
     */ fetchPlannerViewData() {
        const tenantActionCreator = this.acLoader.load(ActionCreator.Tenant);
        const userActionCreator = this.acLoader.load(ActionCreator.User);
        const capabilitiesActionCreator = this.acLoader.load(ActionCreator.Capabilities);
        const fetches = [
            userActionCreator.fetchRoamingUserData(),
            tenantActionCreator.fetchTenantFeedbackSettingsFlow(),
            userActionCreator.fetchUserSettings(),
            tenantActionCreator.fetchTenantGroupSettings(),
            capabilitiesActionCreator.fetchPlannerCapabilities(),
            tenantActionCreator.fetchTenantFeatureSettings()
        ];
        if (this.configProvider().flights.EnableCatalystIntegration) {
            const copilotActionCreator = this.acLoader.load(ActionCreator.Copilot);
            fetches.push(copilotActionCreator.getCopilotEligibility());
        }
        Promise.all(fetches);
    }
    /**
     * Fetch upsell data
     */ fetchUpsellData() {
        const upsellActionCreator = this.acLoader.load(ActionCreator.Upsell);
        upsellActionCreator.fetchProjectSubscriptionStatus();
    }
    /**
     * Evaluate whether the current invocation should fetch data, only sync `lastRoute`, or skip entirely.
     * See {@link FetchDecision} for the meaning of each returned value.
     *
     * @param isBackgroundSync Indicates if the fetch is triggered from a scheduled trigger
     */ evaluateFetchDecision(isBackgroundSync) {
        const isPrefetcherRunning = !this.configProvider().settings.disablePreFetcher && !(window.dataPreFetchCompleted || window.dataPreFetchSkipped);
        const isAppInBackground = this.appContextStore.isAppInBackground();
        // If the app is in background or pre-fetcher is still running, we should not fetch the data
        if (isAppInBackground || isPrefetcherRunning) {
            this.loggers.traceLogger.logTrace(0x1e0dd00b /* tag_4d3al */ , TraceLevel.Info, `Skipping data fetch [isAppInBackground=${isAppInBackground}][isPrefetcherRunning=${isPrefetcherRunning}]`);
            return "skip";
        }
        // Scheduled trigger: only fetch when there isn't already a fetch in flight for the view.
        if (isBackgroundSync) {
            const currentSubRouteType = this.routeStore.getCurrentSubRouteType();
            const currentSubRoute = this.routeStore.getCurrentSubRoute();
            const viewId = extractPrimaryViewIdFromRoute(currentSubRouteType, currentSubRoute, this.appContextStore.getCurrentUserIdentitySet().user.id);
            const state = this.viewLifecycleStore.getViewDataFetchState(viewId);
            return state !== ViewDataFetchState.Started ? "fetch" : "skip";
        }
        // User-driven navigation: decide based on the route delta against `lastRoute`.
        return this.shouldFetchData() ? "fetch" : "syncRouteOnly";
    }
    /**
     * Check whether the route delta against `lastRoute` is large enough to warrant a data fetch.
     */ shouldFetchData() {
        const currentSubRouteType = this.routeStore.getCurrentSubRouteType();
        const currentSubRoute = this.routeStore.getCurrentSubRoute();
        const hasSubRouteTypeChanged = currentSubRouteType !== this.lastRoute?.subRouteType;
        const entityIdInLastRoute = this.lastRoute && extractEntityRoutingIdFromSubRoute(this.lastRoute.subRoute, this.lastRoute.subRouteType);
        const entityIdInCurrentRoute = extractEntityRoutingIdFromSubRoute(currentSubRoute, currentSubRouteType);
        const hasNavigatedBetweenTwoPlans = !hasSubRouteTypeChanged && entityIdInCurrentRoute != null && entityIdInLastRoute != null && entityIdInCurrentRoute !== entityIdInLastRoute;
        const taskIdInLastRoute = this.lastRoute && getTaskIdFromSubRoute(this.lastRoute.subRoute);
        const taskIdInCurrentRoute = getTaskIdFromSubRoute(currentSubRoute);
        const hasNavigatedToNewTask = taskIdInCurrentRoute != null && taskIdInCurrentRoute !== taskIdInLastRoute;
        // Has the app moved to foreground (current state - app in foreground) from background (previous state app was in background)
        const hasAppMovedToForeground = !this.appContextStore.isAppInBackground() && this.wasAppInBackground;
        // 1. Typically, only subroute change should not trigger data fetch to avoid unnecessary service call.
        // 2. When routing between two same plan types, the subRouteType will not change, but the planId may change and we need to fetch data.
        // 3. When routing to a new task, we need to fetch data.
        // 4. MyTasksViewActionCreator fetches data based on the subRoute, so data must be fetched on every MyTasks subroute change.
        // 5. App moves from background to foreground
        const shouldFetchData = hasSubRouteTypeChanged || hasNavigatedBetweenTwoPlans || hasNavigatedToNewTask || currentSubRouteType === SubRouteType.MyTasks || hasAppMovedToForeground;
        return shouldFetchData;
    }
    /**
     * Check if the current navigation is a task-only navigation within the same view.
     * Returns true when only the task ID changed (e.g., opening/switching task panel)
     * while the view type and primary entity (plan) remain the same.
     * Excludes MyTasks since its view action creator fetches data based on the full subRoute.
     */ isTaskOnlyNavigation(currentSubRouteType) {
        if (!this.lastRoute) {
            return false;
        }
        const currentSubRoute = this.routeStore.getCurrentSubRoute();
        // SubRoute type must be the same
        if (currentSubRouteType !== this.lastRoute.subRouteType) {
            return false;
        }
        // MyTasks always needs full view fetch (its AC fetches based on subRoute)
        if (currentSubRouteType === SubRouteType.MyTasks) {
            return false;
        }
        // Must not have navigated between different plans/entities.
        // Treat null/non-null mismatch as an entity change to avoid skipping fetch on malformed routes.
        const entityIdInLastRoute = extractEntityRoutingIdFromSubRoute(this.lastRoute.subRoute, this.lastRoute.subRouteType);
        const entityIdInCurrentRoute = extractEntityRoutingIdFromSubRoute(currentSubRoute, currentSubRouteType);
        if (entityIdInCurrentRoute !== entityIdInLastRoute) {
            return false;
        }
        // Must have navigated to a new/different task
        const taskIdInLastRoute = getTaskIdFromSubRoute(this.lastRoute.subRoute);
        const taskIdInCurrentRoute = getTaskIdFromSubRoute(currentSubRoute);
        if (taskIdInCurrentRoute == null || taskIdInCurrentRoute === taskIdInLastRoute) {
            return false;
        }
        // Skip optimization when force-fetch is pending (e.g., delta token expiry, stale prefetch),
        // since the view data methods consume force-fetch state.
        const viewId = extractPrimaryViewIdFromRoute(currentSubRouteType, currentSubRoute, this.appContextStore.getCurrentUserIdentitySet().user.id);
        if (this.didPrefetchFailForView(viewId) || this.viewLifecycleStore.getViewForceDataFetchState(viewId)) {
            return false;
        }
        return true;
    }
    /**
     * Fetch data for the LeftNav view
     */ fetchLeftNavViewData() {
        // If preFetch failed for mru, we force fetch left nav data
        // Todo: bug 2835757 to handle disablePrefetcher centrally for all views
        // Currently there is no polling on fetchLeftNavViewData and also disablePreFetcher is false so we won't run into the scenario of too many calls
        const force = window.dataPreFetchFailedForMru === true || this.configProvider().settings.disablePreFetcher;
        if (this.shouldFetchDataForLeftNav()) {
            const leftNavViewAc = this.acLoader.load(ActionCreator.LeftNavView);
            leftNavViewAc.fetchViewData(force);
        }
    }
    /**
     * Fetch data for the SuiteNav view
     */ fetchSuiteNavViewData() {
        const clientType = this.configProvider().sessionMetaData.appMetadata.clientType;
        if (clientType !== ClientType.Web) {
            return;
        }
        const suiteNavViewAc = this.acLoader.load(ActionCreator.SuiteNavView);
        suiteNavViewAc.fetchViewData();
    }
    /**
     * Fetch data for the MyDay view
     */ fetchMyDayViewData() {
        const myDayViewAc = this.acLoader.load(ActionCreator.MyDayView);
        myDayViewAc.fetchViewData(this.shouldForceFetchData());
    }
    /**
     * Fetch data for the MyTasks view
     */ fetchMyTasksViewData(isBackgroundSync) {
        const currentSubRoute = this.routeStore.getCurrentSubRoute();
        const myTasksRoutingDetails = extractMyTasksRoutingDetailsFromSubRoute(currentSubRoute, SubRouteType.MyTasks);
        if (!myTasksRoutingDetails) {
            return;
        }
        const myTasksViewAc = this.acLoader.load(ActionCreator.MyTasksView);
        /**
         * MyTasks view load is the initialLoad if any of the following conditions are met:
         * 1. It it the initial route i.e lastRoute is undefined
         * 2. The last route is not MyTasks view
         * 3. The last route is MyTasks view but current route doesn't have taskId in it i.e. it is a pivot change
         */ const isInitialLoad = this.lastRoute === undefined || this.lastRoute.subRouteType !== SubRouteType.MyTasks || !myTasksRoutingDetails.task;
        myTasksViewAc.fetchViewData(myTasksRoutingDetails.pivotType, isInitialLoad, this.shouldForceFetchData(), isBackgroundSync);
    }
    /**
     * Fetch data for the MyPlans view
     */ fetchMyPlansViewData(isBackgroundSync) {
        const myPlansViewAc = this.acLoader.load(ActionCreator.MyPlansView);
        myPlansViewAc.fetchViewData(isBackgroundSync, this.shouldForceFetchData());
    }
    /**
     * Fetch data for the MyPortfolios view
     */ fetchMyPortfoliosViewData() {
        const myPortfoliosViewAc = this.acLoader.load(ActionCreator.MyPortfoliosView);
        myPortfoliosViewAc.fetchViewData(this.shouldForceFetchData());
    }
    /**
     * Fetch data for the CreateTaskFromMessage view
     */ fetchCreateTaskFromMessageViewData(isBackgroundSync) {
        const createTaskFromMessageViewAc = this.acLoader.load(ActionCreator.CreateTaskView);
        createTaskFromMessageViewAc.fetchViewData(!isBackgroundSync /* do not force fetch on scheduled triggers */ );
    }
    /**
     * Fetch data for the plan view
     * @param isBackgroundSync Indicates if the fetch is triggered from a scheduled trigger
     */ fetchPlanViewData(isBackgroundSync) {
        const subRouteType = this.routeStore.getCurrentSubRouteType();
        const subRoute = this.routeStore.getCurrentSubRoute();
        const planRoutingDetails = extractPlanRoutingDetailsFromSubRoute(subRoute, subRouteType);
        if (!planRoutingDetails) {
            return;
        }
        const planViewAc = this.acLoader.load(ActionCreator.PlanView);
        const viewId = extractPrimaryViewIdFromRoute(subRouteType, subRoute, this.appContextStore.getCurrentUserIdentitySet().user.id);
        const forceFetch = this.consumeViewForceFetchState(viewId);
        planViewAc.fetchViewData(planRoutingDetails.planId, forceFetch, isBackgroundSync);
    }
    /**
     * Fetch data for the task editor view
     * @param isBackgroundSync Indicates if the fetch is triggered from a scheduled trigger
     * @param fetchTaskDataOnly When true, skips plan-scoped collection fetches (buckets, goals, etc.)
     * @param shouldFetchTasksForDependency When true, also fetches tasks for the entire plan to ensure dependencies are up to date in the task editor (used when switching to the Planning tab)
     */ fetchTaskEditorViewData(isBackgroundSync, fetchTaskDataOnly, shouldFetchTasksForDependency = false) {
        const subRoute = this.routeStore.getCurrentSubRoute();
        const taskId = getTaskIdFromSubRoute(subRoute);
        if (taskId) {
            const taskEditorViewActionCreator = this.acLoader.load(ActionCreator.TaskEditorView);
            taskEditorViewActionCreator.fetchViewData(taskId, isBackgroundSync, undefined, fetchTaskDataOnly, shouldFetchTasksForDependency);
        }
    }
    /**
     * Update the last route
     */ updateLastRoute() {
        const route = {
            rootUrl: "",
            subRouteType: this.routeStore.getCurrentSubRouteType(),
            subRoute: this.routeStore.getCurrentSubRoute()
        };
        this.lastRoute = route;
    }
    /**
     * Update the app background state
     */ updateAppBackgroundState() {
        this.wasAppInBackground = this.appContextStore.isAppInBackground();
    }
    /**
     * Subscribe to data pre-fetcher completion event
     */ async waitForDataPreFetchCompletion() {
        // If pre-fetch is already skipped, do nothing
        if (window.dataPreFetchSkipped) {
            this.loggers.traceLogger.logTrace(0x1e0dd00a /* tag_4d3ak */ , TraceLevel.Info, "Continue with sync as pre-fetch is already skipped");
            return;
        }
        // If pre-fetch is already completed, store the pre-fetched data
        if (window.dataPreFetchCompleted) {
            this.loggers.traceLogger.logTrace(0x1e0dd009 /* tag_4d3aj */ , TraceLevel.Info, "Continue with sync as pre-fetch is already Completed");
            return;
        }
        // Subscribe to pre-fetcher completion and skipped events to wait on them
        await new Promise((resolve)=>{
            const handleCompletion = ()=>{
                this.loggers.traceLogger.logTrace(0x1e0dd008 /* tag_4d3ai */ , TraceLevel.Info, "Pre-fetch completion notified");
                resolve();
            };
            const handleSkipped = ()=>{
                this.loggers.traceLogger.logTrace(0x1e0dd007 /* tag_4d3ah */ , TraceLevel.Info, "Pre-fetch skip notified");
                resolve();
            };
            window.addEventListener(DataPreFetcherEventNames.FetchCompleted, handleCompletion, {
                once: true
            });
            window.addEventListener(DataPreFetcherEventNames.FetchSkipped, handleSkipped, {
                once: true
            });
        });
    }
    /**
     * Get the polling interval based on Trouter connection state.
     * - When Trouter is connected: use longer interval (real-time updates via push)
     * - When Trouter is disconnected: use shorter interval (polling is primary mechanism)
     */ getPollingIntervalMs() {
        const { trouterSettings, deltaSyncSettings } = this.configProvider().settings;
        const { EnableTrouterIntegration } = this.configProvider().flights;
        // If Trouter integration is disabled, always use normal interval
        if (!EnableTrouterIntegration || trouterSettings.disable) {
            return deltaSyncSettings.syncInterval * 1000;
        }
        const isTrouterConnected = this.trouterStore.getConnectionState().isConnected;
        if (isTrouterConnected) {
            // Trouter connected: longer fixed interval (real-time updates via push)
            return trouterSettings.trouterModeDeltaSyncIntervalInSeconds * 1000;
        } else {
            // Trouter disconnected: shorter interval (polling is primary mechanism)
            return deltaSyncSettings.syncInterval * 1000;
        }
    }
    /**
     * Restart polling with new interval, optionally polling immediately.
     * @param pollImmediately Whether to poll immediately before starting the new timer
     */ restartDataFetchPolling(pollImmediately) {
        // CRITICAL: Stop existing timer first to prevent cascading polls
        this.stopDataFetchPolling();
        // Poll immediately if requested (before starting new timer)
        if (pollImmediately) {
            this.fetchViewDataBasedOnRoute(true);
        }
        // Start new timer with appropriate interval
        this.startDataFetchPolling();
    }
    /**
     * Start the periodic data fetch timer.
     * This will fetch the deltas, if we already have the delta tokens for the collection.
     */ startDataFetchPolling() {
        const { disable } = this.configProvider().settings.deltaSyncSettings;
        // If delta sync is disabled, timer is already running, or auth is persistently failing, do not start another timer
        if (disable || this.pollingTimerId !== undefined || this.authStore.isAuthPersistentlyFailing()) {
            return;
        }
        this.loggers.traceLogger.logTrace(0x1e019498 /* tag_4azsy */ , TraceLevel.Info, `Starting data fetch polling [TrouterConnected=${this.trouterStore.getConnectionState().isConnected}]`);
        this.scheduleNextPoll();
    }
    /**
     * Schedule the next poll using setTimeout.
     * Recalculates interval each tick so changes in Trouter connection state are
     * self-correcting — even if a state change notification is missed, the next
     * tick will use the correct interval.
     */ scheduleNextPoll() {
        const intervalMs = this.getPollingIntervalMs();
        const isTrouterConnected = this.trouterStore.getConnectionState().isConnected;
        this.loggers.traceLogger.logTrace(0x1e061562 /* tag_4b7v8 */ , TraceLevel.Info, `Scheduling next data fetch poll [IntervalMs=${intervalMs}][TrouterConnected=${isTrouterConnected}]`);
        this.pollingTimerId = window.setTimeout(()=>{
            this.pollingTimerId = undefined;
            if (this.isUserActive()) {
                this.fetchViewDataBasedOnRoute(true);
            }
            // Re-schedule only if app is still in foreground
            if (!this.appContextStore.isAppInBackground()) {
                this.scheduleNextPoll();
            }
        }, intervalMs);
    }
    /**
     * Stop the periodic data fetch timer
     */ stopDataFetchPolling() {
        if (this.pollingTimerId !== undefined) {
            window.clearTimeout(this.pollingTimerId);
            this.pollingTimerId = undefined;
        }
    }
    /**
     * Calculates whether a force fetch is needed for the primary view on the current route in the current fetch pass
     */ shouldForceFetchData() {
        const subRouteType = this.routeStore.getCurrentSubRouteType();
        const subRoute = this.routeStore.getCurrentSubRoute();
        const userId = this.appContextStore.getCurrentUserIdentitySet().user.id;
        const viewId = extractPrimaryViewIdFromRoute(subRouteType, subRoute, userId);
        return this.consumeViewForceFetchState(viewId);
    }
    /**
     * method to get the current force fetch status for the view and reset it as needed
     * @param viewName name of the view
     * @param viewId id of the view
     */ consumeViewForceFetchState(viewId) {
        /**
         * Force fetch will be needed in below cases:
         * 1. Pre fetch failed for the route, force fetch needed to get the latest data from service, force fetch set to false on default route setup.
         * 2. Route change in running/cached sessions where data is present in memory but data is not latest.
         * 3. Delta token expiry where fresh data is needed in running sessions to ensure latest data for the user.
         * 1 is taken care of with the window check. 2&3 are handled by the store check.
         */ const forceFetch = this.didPrefetchFailForView(viewId) || this.viewLifecycleStore.getViewForceDataFetchState(viewId);
        if (forceFetch) {
            this.acLoader.load(ActionCreator.ViewLifecycle).viewForceFetch(viewId, false);
            window.dataPreFetchFailedForView = undefined;
        }
        return forceFetch;
    }
    /**
     * method to check if preFetch failed for a given viewName and viewId
     * @param viewName name of the view
     * @param viewId id of the view
     */ didPrefetchFailForView(viewId) {
        return window.dataPreFetchViewName === viewId.name && window.dataPreFetchViewId === viewId.partitionKey && window.dataPreFetchFailedForView === true;
    }
    /**
     * Constructor for the DataFetchWorker
     * @param acLoader The action creator loader
     * @param storeLoader The store loader
     * @param loggers The logger context
     * @param configProvider The config provider
     */ constructor(acLoader, storeLoader, loggers, configProvider){
        this.acLoader = acLoader;
        this.storeLoader = storeLoader;
        this.loggers = loggers;
        this.configProvider = configProvider;
        /** Indicates if the worker has been initialized */ this.initialized = false;
        /** The route store */ this.routeStore = this.storeLoader.load(Store.Route);
        /** The view lifecycle store */ this.viewLifecycleStore = this.storeLoader.load(Store.ViewLifecycle);
        /** The app context store */ this.appContextStore = this.storeLoader.load(Store.AppContext);
        /** The Trouter store for connection state */ this.trouterStore = this.storeLoader.load(Store.Trouter);
        /** The auth store for auth failure state */ this.authStore = this.storeLoader.load(Store.Auth);
        /** The view store for task editor tab selection */ this.viewStore = this.storeLoader.load(Store.View);
        /** The activity store — for checking when the user was active last */ this.activityStore = this.storeLoader.load(Store.Activity);
        /** The task store — used to trigger task history refetches on task changes while the history pane is open */ this.taskStore = this.storeLoader.load(Store.Tasks);
        /** The last route */ this.lastRoute = undefined;
        /** Last state for app (background/foreground) */ this.wasAppInBackground = false;
        /** Last known Trouter connection state */ this.lastTrouterConnectedState = false;
        /** Timer ID for periodic data fetch */ this.pollingTimerId = undefined;
        /** Tracks whether polling was paused due to auth failures */ this.wasPausedForAuth = false;
        /** The last known task editor tab to detect tab changes */ this.lastTaskEditorSelectedTab = undefined;
        /** The task we last fetched history for, so repeated store notifications for the same task don't refetch */ this.lastFetchedHistoryTask = undefined;
        /** Pending debounce timer id for trouter connection state changes */ this.trouterDebounceTimerId = undefined;
        /**
     * Fetch the data for the background state
     */ this.handleAppBackgroundStateUpdate = async ()=>{
            // Has the app moved to foreground (current state - app in foreground) from background (previous state app was in background)
            const hasAppMovedToForeground = !this.appContextStore.isAppInBackground() && this.wasAppInBackground;
            // Has the app moved to background (current state - app in background) from foreground (previous state app was in foreground)
            const hasAppMovedToBackground = this.appContextStore.isAppInBackground() && !this.wasAppInBackground;
            // Update the local state on the worker. Update it after resolving hasAppMovedToForeground to ensure the correct state is captured
            this.updateAppBackgroundState();
            // 1. Fetch the data and start data fetch polling when the app is transitioning states from background to foreground.
            // 2. Stop the data fetch polling when the app is transitioning states from foreground to background.
            if (hasAppMovedToForeground) {
                // Re-baseline trouter state so the inevitable disconnect→reconnect resume flap
                // is treated as "no net change" by the debounce evaluator.
                this.lastTrouterConnectedState = this.trouterStore.getConnectionState().isConnected;
                this.fetchPlannerViewData();
                this.fetchUpsellData();
                this.fetchViewDataBasedOnRoute();
                this.fetchLeftNavViewData();
                this.startDataFetchPolling();
            } else if (hasAppMovedToBackground) {
                // Cancel any pending trouter debounce — trouter activity in background isn't actionable
                // and a stale timer firing in background could silently desync lastTrouterConnectedState.
                this.clearTrouterDebounceTimer();
                this.stopDataFetchPolling();
            }
        };
        /**
     * Handle Trouter connection state changes.
     *
     * State-change events can arrive in rapid bursts (e.g., a browser tab returning
     * from background often produces disconnect→reconnect within ~1s). To avoid
     * redundant `restartDataFetchPolling(true)` calls on flapping events, we debounce —
     * the actual evaluation runs in `evaluateTrouterStateChange` after
     * `connectionStateDebounceMs` has elapsed without further state changes.
     */ this.handleTrouterConnectionStateChange = ()=>{
            this.clearTrouterDebounceTimer();
            const debounceMs = this.configProvider().settings.trouterSettings.connectionStateDebounceMs ?? 60000;
            this.trouterDebounceTimerId = window.setTimeout(()=>{
                this.trouterDebounceTimerId = undefined;
                this.evaluateTrouterStateChange();
            }, debounceMs);
        };
        /**
     * Handle auth state changes.
     * Pauses data fetch polling when auth is persistently failing,
     * and resumes when auth recovers.
     */ this.handleAuthStateChange = ()=>{
            const isPersistentlyFailing = this.authStore.isAuthPersistentlyFailing();
            if (isPersistentlyFailing && !this.wasPausedForAuth) {
                this.wasPausedForAuth = true;
                this.stopDataFetchPolling();
                this.loggers.traceLogger.logTrace(0x1e01e612 /* tag_4a4ys */ , TraceLevel.Warning, "Data fetch polling paused due to persistent auth token failures");
            } else if (!isPersistentlyFailing && this.wasPausedForAuth) {
                this.wasPausedForAuth = false;
                this.loggers.traceLogger.logTrace(0x1e01e611 /* tag_4a4yr */ , TraceLevel.Info, "Data fetch polling resuming after auth recovery");
                this.restartDataFetchPolling(true);
            }
        };
        /**
     * Handle view store changes for task-editor-driven data fetches.
     * Routes to the dependencies tab-change fetch and the task history fetch, each of which
     * applies its own flight gate internally.
     */ this.handleViewStoreChange = ()=>{
            if (this.configProvider().flights.EnableDependencies) {
                this.handleTaskEditorTabChange();
            }
            this.handleTaskChangeForHistory();
        };
        /**
     * Fetch history for the open task while the History pane is open. Called from the view, task, and
     * route store callbacks, so history follows the task whichever store changed (pane open, route
     * switch, or task edit).
     *
     * Only fetches when the task actually changed - a different task or the same task edited - so
     * repeated store notifications don't over-fetch. Flight/permission and freshness are enforced
     * downstream by the action creator and module.
     */ this.handleTaskChangeForHistory = ()=>{
            if (!this.configProvider().flights.EnableTaskHistory) {
                return;
            }
            // Only load history while the History pane is open. When it's closed, leave the tracking in
            // place: any history-generating edit replaces the Task object (lastModifiedDateTime alone
            // changes it), so the reference check below still refetches on reopen if the task changed,
            // and skips a redundant fetch when it didn't.
            if (this.viewStore.getTaskEditorActiveSidePanel() !== TaskEditorSidePanelType.History) {
                return;
            }
            const taskId = getTaskIdFromSubRoute(this.routeStore.getCurrentSubRoute());
            if (!taskId) {
                return;
            }
            const task = this.taskStore.getTask(taskId).data;
            if (task == null) {
                return;
            }
            // A different task or an edited task both yield a new task object, so a single reference check
            // covers "switched task" and "task changed" without also tracking the id.
            if (task === this.lastFetchedHistoryTask) {
                return;
            }
            this.lastFetchedHistoryTask = task;
            this.acLoader.load(ActionCreator.History).fetchTaskHistory(task.planId, taskId);
        };
        /**
     * Fetch view data based on the current route
     * @param isBackgroundSync Indicates if the fetch is triggered from a scheduled trigger
     */ this.fetchViewDataBasedOnRoute = (isBackgroundSync = false)=>{
            const decision = this.evaluateFetchDecision(isBackgroundSync);
            if (decision === "skip") {
                return;
            }
            if (decision === "syncRouteOnly") {
                // Route hasn't changed enough to warrant a fetch, but still refresh lastRoute to stay in sync.
                this.updateLastRoute();
                return;
            }
            // Keep the History pane in sync with the routed task. Placed after the Skip/SyncRouteOnly
            // gates so it honors the same over-fetch guards (background, prefetcher). Pane-open and
            // task-edit are covered by the view- and task-store subscriptions, so nothing is lost here.
            this.handleTaskChangeForHistory();
            const currentSubRouteType = this.routeStore.getCurrentSubRouteType();
            // When only the task ID changed within the same view (e.g., opening a task panel
            // on the same plan), skip the full view data fetch. The view data (tasks, buckets, etc.)
            // is already fresh from the initial load, Trouter push notifications, or periodic polling.
            // Only fetch the task editor data for the newly selected task.
            // Skip this optimization when force-fetch is pending (e.g., delta token expiry, stale prefetch)
            // since the view data methods consume force-fetch state.
            if (!isBackgroundSync && this.isTaskOnlyNavigation(currentSubRouteType)) {
                this.loggers.traceLogger.logTrace(0x1e00125a /* tag_4abj0 */ , TraceLevel.Info, "Task-only navigation detected, skipping full view data fetch");
                this.fetchTaskEditorViewData(isBackgroundSync, /* fetchTaskDataOnly */ true);
                this.updateLastRoute();
                this.updateAppBackgroundState();
                return;
            }
            switch(currentSubRouteType){
                case SubRouteType.MyDay:
                    this.fetchMyDayViewData();
                    this.fetchTaskEditorViewData(isBackgroundSync);
                    break;
                case SubRouteType.MyTasks:
                    this.fetchMyTasksViewData(isBackgroundSync);
                    this.fetchTaskEditorViewData(isBackgroundSync);
                    break;
                case SubRouteType.MyPlans:
                    this.fetchMyPlansViewData(isBackgroundSync);
                    break;
                case SubRouteType.MyPortfolios:
                    this.fetchMyPortfoliosViewData();
                    break;
                case SubRouteType.BasicPlan:
                case SubRouteType.TodoList:
                    this.fetchPlanViewData(isBackgroundSync);
                    this.fetchTaskEditorViewData(isBackgroundSync);
                    break;
                case SubRouteType.MessageTasks:
                    this.fetchCreateTaskFromMessageViewData(isBackgroundSync);
                    break;
                default:
                    break;
            }
            this.updateLastRoute();
            this.updateAppBackgroundState();
        };
    }
}
